import { useCheckoutSession } from '@api/hooks/useCheckoutSession';
import { useCurrentSubscription } from '@api/hooks/useSubscription';
import { Button } from '@components/common/Button';

interface Props {
  onSkip?: () => void;
}

export function PaymentMethodStep({ onSkip }: Props) {
  const sub = useCurrentSubscription();
  const checkout = useCheckoutSession();

  if (sub.isLoading || !sub.data) {
    return (
      <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
        Loading subscription…
      </div>
    );
  }

  const s = sub.data;
  const requires =
    s.plan?.requires_payment_method &&
    s.billing_mode === 'card' &&
    !s.payment_method_on_file_at;

  if (!requires) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600 dark:text-gray-400">
          No payment method required for your plan right now.
        </p>
        {onSkip && (
          <Button className="mt-4" onClick={onSkip}>
            Continue
          </Button>
        )}
      </div>
    );
  }

  const start = async () => {
    try {
      const res = await checkout.mutateAsync({
        mode: 'setup',
        return_url: `${window.location.origin}/onboarding/payment/return`,
        cancel_url: `${window.location.origin}/onboarding/payment?canceled=1`,
      });
      window.location.href = res.url;
    } catch {
      // BillingBlockedError or other; notification interceptor will surface it
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 text-center">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50">
        Add a payment method
      </h2>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        We&apos;ll save your card on file. Your trial continues — you won&apos;t be charged
        until it ends.
      </p>
      <Button
        onClick={() => void start()}
        disabled={checkout.isPending}
        loading={checkout.isPending}
        className="mt-6 w-full"
      >
        {checkout.isPending ? 'Opening secure checkout…' : 'Add payment method'}
      </Button>
    </div>
  );
}
